import React from 'react';
import {Col, Row, Tabs} from 'antd';
import {FileTextOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';

import PageTitle from '../../../components/page/PageTitle';
import ErrorBoundary from '../../../components/ErrorBoundary';
import ReportsCard from '../../../components/reports/ReportsCard';
import {
    accountBalanceEmptyReportText,
    accountingBalanceHistoryReportInstructionText,
    accountPaymentReportInstructionText,
    accountRevenuePaymentEmptyReportText,
    accountRevenueReportInstructionText,
    bookingHistoryReportInstructionText,
    ctgInvoicesInstructionText,
    ctgTaxesInstructionText,
    depositBalanceHistoryReportInstructionText,
    depositTransactionEmptyReportText,
    depositTransactionHistoryReportInstructionText,
    paymentByTypeReportInstructionText,
    rcsBookingsInstructionText,
    revenueByTypeReportInstructionText,
    shareBalanceEmptyReportText,
    shareBalanceHistoryReportInstructionText,
    shareTransactionEmptyReportText,
    shareTransactionHistoryReportInstructionText
} from '../../../helpers/text';
import {ReportsTab} from '../../../types/reportTypes';

type Carshare = 'regina' | 'cartogo'

const Reports = () => {
    const defaultStartOfPreviousMonth = dayjs().subtract(1, 'months').startOf('month');
    const defaultEndOfPreviousMonth = dayjs().subtract(1, 'months').endOf('month');
    const defaultOneMonthAgo = dayjs().subtract(1, 'months');
    const defaultToday = dayjs();
    const clientName = window.env.CLIENT_NAME as Carshare;

    const tabsItems: ReportsTab[] = [
        {
            label: 'Bookings',
            key: '2',
            children: (
                <ErrorBoundary>
                    <Row gutter={[{xs: 0, md: 16}, 0]}>
                        <Col xs={24} md={8}>
                            <ReportsCard
                                cardDescription={bookingHistoryReportInstructionText}
                                cardTitle={'Booking History'}
                                emptyReportText={null}
                                reportName={'Booking_history'}
                                defaultStartDate={defaultStartOfPreviousMonth}
                                defaultEndDate={defaultEndOfPreviousMonth}
                            />
                        </Col>
                    </Row>
                </ErrorBoundary>
            )
        },
        {
            label: 'Balances',
            key: '3',
            children: (
                <ErrorBoundary>
                    <Row gutter={[{xs: 0, md: 16}, 16]}>
                        <Col xs={24} md={8}>
                            <ReportsCard
                                cardDescription={accountingBalanceHistoryReportInstructionText}
                                cardTitle={'Accounting Balance History'}
                                emptyReportText={accountBalanceEmptyReportText}
                                reportName={'Accounting_balance_history'}
                                defaultStartDate={defaultOneMonthAgo}
                                defaultEndDate={defaultToday}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <ReportsCard
                                cardDescription={shareBalanceHistoryReportInstructionText}
                                cardTitle={'Share Balance History'}
                                emptyReportText={shareBalanceEmptyReportText}
                                reportName={'Share_balance_history'}
                                defaultStartDate={defaultOneMonthAgo}
                                defaultEndDate={defaultToday}
                                shareClassExists={true}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <ReportsCard
                                cardDescription={shareTransactionHistoryReportInstructionText}
                                cardTitle={'Share Transaction History'}
                                emptyReportText={shareTransactionEmptyReportText}
                                reportName={'Share_transaction_history'}
                                defaultStartDate={defaultOneMonthAgo}
                                defaultEndDate={defaultToday}
                                shareClassExists={true}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <ReportsCard
                                cardDescription={depositBalanceHistoryReportInstructionText}
                                cardTitle={'Deposit Balance History'}
                                emptyReportText={accountBalanceEmptyReportText}
                                reportName={'Deposit_balance_history'}
                                defaultStartDate={defaultOneMonthAgo}
                                defaultEndDate={defaultToday}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <ReportsCard
                                cardDescription={depositTransactionHistoryReportInstructionText}
                                cardTitle={'Deposit Transaction History'}
                                emptyReportText={depositTransactionEmptyReportText}
                                reportName={'Deposit_transaction_history'}
                                defaultStartDate={defaultOneMonthAgo}
                                defaultEndDate={defaultToday}
                            />
                        </Col>
                    </Row>
                </ErrorBoundary>
            )
        },
        {
            label: 'Revenue',
            key: '4',
            children: (
                <ErrorBoundary>
                    <Row gutter={[{xs: 0, md: 16}, 0]}>
                        <Col xs={24} md={8}>
                            <ReportsCard
                                cardDescription={accountRevenueReportInstructionText}
                                cardTitle={'Revenue By Account'}
                                emptyReportText={accountRevenuePaymentEmptyReportText}
                                reportName={'Account_revenue'}
                                defaultStartDate={defaultStartOfPreviousMonth}
                                defaultEndDate={defaultEndOfPreviousMonth}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <ReportsCard
                                cardDescription={revenueByTypeReportInstructionText}
                                cardTitle={'Revenue By Type'}
                                emptyReportText={null}
                                reportName={'Revenue_by_type'}
                                defaultStartDate={defaultStartOfPreviousMonth}
                                defaultEndDate={defaultEndOfPreviousMonth}
                            />
                        </Col>
                    </Row>
                </ErrorBoundary>
            )
        },
        {
            label: 'Payments',
            key: '5',
            children: (
                <ErrorBoundary>
                    <Row gutter={[{xs: 0, md: 16}, 0]}>
                        <Col xs={24} md={8}>
                            <ReportsCard
                                cardDescription={accountPaymentReportInstructionText}
                                cardTitle={'Payment By Account'}
                                emptyReportText={accountRevenuePaymentEmptyReportText}
                                reportName={'Account_payment'}
                                defaultStartDate={defaultStartOfPreviousMonth}
                                defaultEndDate={defaultEndOfPreviousMonth}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <ReportsCard
                                cardDescription={paymentByTypeReportInstructionText}
                                cardTitle={'Payment By Type'}
                                emptyReportText={null}
                                reportName={'Payment_by_type'}
                                defaultStartDate={defaultStartOfPreviousMonth}
                                defaultEndDate={defaultEndOfPreviousMonth}
                            />
                        </Col>
                    </Row>
                </ErrorBoundary>
            )
        }
    ];

    if (clientName === 'regina') {
        let clientCSOTab = {
            label: 'Regina Car Share',
            key: '1',
            children: (
                 <ErrorBoundary>
                    <Row gutter={[{xs: 0, md: 16}, 0]}>
                        <Col xs={24} md={8}>
                            <ReportsCard
                                cardDescription={rcsBookingsInstructionText}
                                cardTitle={'Bookings Report'}
                                emptyReportText={null}
                                reportName={'rcs_bookings'}
                                defaultStartDate={defaultStartOfPreviousMonth}
                                defaultEndDate={defaultEndOfPreviousMonth}
                                disableDatePickers={true}
                            />
                        </Col>
                    </Row>
                </ErrorBoundary>
            )
        }
        tabsItems.unshift(clientCSOTab)
    } else if (clientName === 'cartogo') {
        let clientCSOTab = {
            label: 'Car to Go',
            key: '1',
            children: (
                 <ErrorBoundary>
                    <Row gutter={[{xs: 0, md: 16}, 0]}>
                        <Col xs={24} md={8}>
                            <ReportsCard
                                cardDescription={ctgTaxesInstructionText}
                                cardTitle={'Taxes report'}
                                emptyReportText={null}
                                reportName={'ctg_taxes'}
                                defaultStartDate={defaultStartOfPreviousMonth}
                                defaultEndDate={defaultEndOfPreviousMonth}
                                disableDatePickers={true}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <ReportsCard
                                cardDescription={ctgInvoicesInstructionText}
                                cardTitle={'Invoices report'}
                                emptyReportText={null}
                                reportName={'ctg_invoices'}
                                defaultStartDate={defaultStartOfPreviousMonth}
                                defaultEndDate={defaultEndOfPreviousMonth}
                                disableDatePickers={true}
                            />
                        </Col>
                    </Row>
                </ErrorBoundary>
            )
        }
        tabsItems.unshift(clientCSOTab)
    }

    return (
        <>
            <PageTitle title="Reports" icon={<FileTextOutlined/>}/>
            <Tabs defaultActiveKey="1" items={tabsItems}/>
        </>
    );
};

export default Reports;
